import React, { useState, useEffect } from 'react';
import { Card } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { Badge } from './ui/badge';
import { Plus, Edit2, Trash2, ShoppingBag, Send, MessageCircle, Mail, CheckCircle, Paperclip } from 'lucide-react';
import { toast } from 'sonner';
import { productos } from '../data/mockData';

const QuoteForm = () => {
  // Estado para la lista de productos en la cotización
  const [productosEnCotizacion, setProductosEnCotizacion] = useState([]);
  
  // Estado para el formulario de agregar producto
  const [formProducto, setFormProducto] = useState({
    tipoPrenda: '',
    talle: '',
    color: '',
    cantidad: 1
  });

  // Estado para talles y colores dinámicos
  const [tallesDisponibles, setTallesDisponibles] = useState([]);
  const [coloresDisponibles, setColoresDisponibles] = useState([]);

  // Estado para datos del cliente
  const [formCliente, setFormCliente] = useState({
    nombre: '',
    email: '',
    telefono: '',
    comentarios: ''
  });

  // Estado para edición
  const [productoEditando, setProductoEditando] = useState(null);

  // Estado para mostrar confirmación
  const [mostrarConfirmacion, setMostrarConfirmacion] = useState(false);
  const [datosEnviados, setDatosEnviados] = useState(null);

  // Verificar si la cotización fue enviada exitosamente
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    if (urlParams.get('cotizacion') === 'enviada') {
      // Recuperar datos del sessionStorage
      const datos = sessionStorage.getItem('cotizacionEnviada');
      if (datos) {
        setDatosEnviados(JSON.parse(datos));
        setMostrarConfirmacion(true);
        sessionStorage.removeItem('cotizacionEnviada');
      }
      window.history.replaceState({}, '', window.location.pathname);
      
      // Scroll a la sección de cotización después de cargar
      setTimeout(() => {
        document.getElementById('cotizacion')?.scrollIntoView({ behavior: 'smooth' });
      }, 100);
    }
  }, []);

  // Actualizar talles y colores cuando cambia el tipo de prenda
  useEffect(() => {
    if (formProducto.tipoPrenda) {
      const productoSeleccionado = productos.find(
        p => p.nombre === formProducto.tipoPrenda
      );
      
      if (productoSeleccionado) {
        setTallesDisponibles(productoSeleccionado.talles || []);
        setColoresDisponibles(productoSeleccionado.colores || []);
        
        // Si solo hay un talle (ej: "Único"), auto-seleccionarlo
        if (productoSeleccionado.talles?.length === 1) {
          setFormProducto(prev => ({
            ...prev,
            talle: productoSeleccionado.talles[0]
          }));
        } else {
          // Resetear talle y color si cambia el producto
          setFormProducto(prev => ({
            ...prev,
            talle: '',
            color: ''
          }));
        }
      }
    } else {
      setTallesDisponibles([]);
      setColoresDisponibles([]);
    }
  }, [formProducto.tipoPrenda]);

  // Agregar producto a la cotización
  const agregarProducto = () => {
    // Validaciones
    if (!formProducto.tipoPrenda) {
      toast.error('Selecciona un tipo de prenda');
      return;
    }
    if (!formProducto.talle) {
      toast.error('Selecciona un talle');
      return;
    }
    if (!formProducto.cantidad || formProducto.cantidad < 1) {
      toast.error('Ingresa una cantidad válida');
      return;
    }

    const colorFinal = formProducto.color || 'Sin especificar';
    const cantidadNueva = parseInt(formProducto.cantidad);

    if (productoEditando) {
      // Actualizar producto existente
      const nuevoProducto = {
        id: productoEditando,
        producto: formProducto.tipoPrenda,
        talle: formProducto.talle,
        color: colorFinal,
        cantidad: cantidadNueva
      };
      setProductosEnCotizacion(
        productosEnCotizacion.map(p => p.id === productoEditando ? nuevoProducto : p)
      );
      toast.success('Producto actualizado');
      setProductoEditando(null);
    } else {
      // Buscar si ya existe un producto con las mismas características
      const productoExistente = productosEnCotizacion.find(
        p => p.producto === formProducto.tipoPrenda && 
             p.talle === formProducto.talle && 
             p.color === colorFinal
      );

      if (productoExistente) {
        // Sumar cantidad al producto existente
        setProductosEnCotizacion(
          productosEnCotizacion.map(p => 
            p.id === productoExistente.id 
              ? { ...p, cantidad: p.cantidad + cantidadNueva }
              : p
          )
        );
        toast.success(`Cantidad actualizada: ${productoExistente.cantidad + cantidadNueva} unidades`);
      } else {
        // Agregar nuevo producto
        const nuevoProducto = {
          id: Date.now(),
          producto: formProducto.tipoPrenda,
          talle: formProducto.talle,
          color: colorFinal,
          cantidad: cantidadNueva
        };
        setProductosEnCotizacion([...productosEnCotizacion, nuevoProducto]);
        toast.success('Producto agregado a la cotización');
      }
    }

    // Resetear formulario
    setFormProducto({
      tipoPrenda: '',
      talle: '',
      color: '',
      cantidad: 1
    });
  };

  // Eliminar producto de la cotización
  const eliminarProducto = (id) => {
    setProductosEnCotizacion(productosEnCotizacion.filter(p => p.id !== id));
    toast.success('Producto eliminado');
  };

  // Editar producto
  const editarProducto = (id) => {
    const producto = productosEnCotizacion.find(p => p.id === id);
    setFormProducto({
      tipoPrenda: producto.producto,
      talle: producto.talle,
      color: producto.color === 'Sin especificar' ? '' : producto.color,
      cantidad: producto.cantidad
    });
    setProductoEditando(id);
    
    // Scroll al formulario
    document.getElementById('form-producto')?.scrollIntoView({ behavior: 'smooth' });
  };

  // Calcular total de prendas
  const totalPrendas = productosEnCotizacion.reduce(
    (sum, p) => sum + p.cantidad,
    0
  );

  const aplicaDescuento = totalPrendas >= 5;

  // Formatear fecha y hora (Uruguay UTC-3)
  const formatearFechaHora = () => {
    const ahora = new Date();
    // Convertir a zona horaria de Uruguay (UTC-3)
    const uruguayTime = new Date(ahora.toLocaleString('en-US', { timeZone: 'America/Montevideo' }));
    const dia = String(uruguayTime.getDate()).padStart(2, '0');
    const mes = String(uruguayTime.getMonth() + 1).padStart(2, '0');
    const anio = uruguayTime.getFullYear();
    const horas = String(uruguayTime.getHours()).padStart(2, '0');
    const minutos = String(uruguayTime.getMinutes()).padStart(2, '0');
    return `${dia}/${mes}/${anio} - ${horas}:${minutos}hs`;
  };

  // Generar resumen de productos para mensajes
  const generarResumenProductos = () => {
    return productosEnCotizacion.map(p => 
      `• ${p.producto} - ${p.talle} ${p.color} x${p.cantidad}`
    ).join('\n');
  };

  // Generar mensaje para WhatsApp
  const generarMensajeWhatsApp = () => {
    const fechaHora = datosEnviados?.fechaHora || formatearFechaHora();
    const nombre = datosEnviados?.nombre || formCliente.nombre;
    const telefono = datosEnviados?.telefono || formCliente.telefono;
    const resumen = datosEnviados?.resumenProductos || generarResumenProductos();
    const total = datosEnviados?.totalPrendas || totalPrendas;
    
    return encodeURIComponent(
      `Hola D-Lyrio! 👋\n\n` +
      `Soy ${nombre} y acabo de enviar una cotización.\n\n` +
      `📅 Fecha: ${fechaHora}\n` +
      `📱 Teléfono: ${telefono}\n\n` +
      `🛍️ Productos solicitados:\n${resumen}\n\n` +
      `Total: ${total} prendas ${total >= 5 ? '✅' : ''}\n\n` +
      `Te adjunto los archivos de diseño.`
    );
  };

  // Generar mensaje para Email
  const generarMensajeEmail = () => {
    const fechaHora = datosEnviados?.fechaHora || formatearFechaHora();
    const nombre = datosEnviados?.nombre || formCliente.nombre;
    const telefono = datosEnviados?.telefono || formCliente.telefono;
    const email = datosEnviados?.email || formCliente.email;
    const resumen = datosEnviados?.resumenProductos || generarResumenProductos();
    const total = datosEnviados?.totalPrendas || totalPrendas;

    const asunto = encodeURIComponent(`Diseños Cotización - ${nombre} - ${fechaHora.split(' - ')[0]}`);
    const cuerpo = encodeURIComponent(
      `Hola D-Lyrio!\n\n` +
      `Adjunto los diseños para mi cotización enviada hoy.\n\n` +
      `Mis datos:\n` +
      `─────────────\n` +
      `Nombre: ${nombre}\n` +
      `Teléfono: ${telefono}\n` +
      `Email: ${email || 'No especificado'}\n` +
      `Fecha: ${fechaHora}\n\n` +
      `Productos:\n` +
      `─────────────\n` +
      `${resumen}\n\n` +
      `Total: ${total} prendas\n\n` +
      `Archivos adjuntos en este email.\n\n` +
      `Saludos!`
    );

    return { asunto, cuerpo };
  };

  // Abrir WhatsApp
  const abrirWhatsApp = () => {
    const mensaje = generarMensajeWhatsApp();
    window.open(`https://wa.me/59898836023?text=${mensaje}`, '_blank');
  };

  // Abrir Email
  const abrirEmail = () => {
    const { asunto, cuerpo } = generarMensajeEmail();
    window.open(`mailto:dlyrioestampados@gmail.com?subject=${asunto}&body=${cuerpo}`, '_self');
  };

  // Enviar cotización
  const handleSubmit = (e) => {
    // Validaciones
    if (productosEnCotizacion.length === 0) {
      e.preventDefault();
      toast.error('Agrega al menos un producto a la cotización');
      return;
    }
    if (!formCliente.nombre || !formCliente.telefono) {
      e.preventDefault();
      toast.error('Completa tus datos de contacto');
      return;
    }

    // Guardar datos en sessionStorage para mostrar después
    const datosParaConfirmacion = {
      nombre: formCliente.nombre,
      telefono: formCliente.telefono,
      email: formCliente.email,
      fechaHora: formatearFechaHora(),
      resumenProductos: generarResumenProductos(),
      totalPrendas: totalPrendas,
      productos: productosEnCotizacion
    };
    sessionStorage.setItem('cotizacionEnviada', JSON.stringify(datosParaConfirmacion));

    toast.success('Enviando cotización...');
  };

  const tiposPrenda = productos.map(p => p.nombre);

  // Si está mostrando confirmación, renderizar pantalla de éxito
  if (mostrarConfirmacion && datosEnviados) {
    return (
      <section id="cotizacion" className="py-24 bg-soft-gray">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <Card className="bg-white border-none shadow-2xl p-8 md:p-12">
            {/* Icono de éxito */}
            <div className="text-center mb-8">
              <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
                <CheckCircle className="text-green-600" size={48} />
              </div>
              <h2 className="text-3xl md:text-4xl font-serif text-charcoal mb-4">
                ¡Cotización Enviada con Éxito!
              </h2>
              <p className="text-warm-gray text-lg">
                Hemos recibido tu solicitud correctamente
              </p>
            </div>

            {/* Información de referencia */}
            <div className="bg-soft-gray rounded-lg p-6 mb-8">
              <p className="text-sm text-warm-gray mb-2">Referencia:</p>
              <p className="text-lg font-semibold text-charcoal">
                {datosEnviados.nombre} - {datosEnviados.fechaHora}
              </p>
              <p className="text-sm text-warm-gray mt-2">
                📱 {datosEnviados.telefono}
              </p>
            </div>

            {/* Resumen de productos */}
            <div className="mb-8">
              <h3 className="text-xl font-serif text-charcoal mb-4">
                Productos Solicitados:
              </h3>
              <div className="bg-soft-gray rounded-lg p-6">
                {datosEnviados.productos.map((p, idx) => (
                  <div key={idx} className="flex justify-between items-center py-2 border-b border-warm-gray/20 last:border-0">
                    <span className="text-charcoal">
                      {p.producto} - {p.talle} {p.color}
                    </span>
                    <span className="font-semibold text-primary-pink">
                      x{p.cantidad}
                    </span>
                  </div>
                ))}
                <div className="flex justify-between items-center pt-4 mt-4 border-t-2 border-primary-pink">
                  <span className="text-lg font-semibold text-charcoal">Total:</span>
                  <span className="text-2xl font-bold text-primary-pink">
                    {datosEnviados.totalPrendas} prendas
                  </span>
                </div>
              </div>
            </div>

            {/* Sección de envío de diseños */}
            <div className="bg-gradient-to-br from-primary-pink/10 to-royal-blue/10 border-2 border-primary-pink/30 rounded-lg p-8 mb-6">
              <div className="text-center mb-6">
                <div className="w-16 h-16 bg-primary-pink rounded-full flex items-center justify-center mx-auto mb-4">
                  <Paperclip className="text-cream-white" size={32} />
                </div>
                <h3 className="text-2xl font-serif text-charcoal mb-3">
                  Ahora Envía tus Diseños
                </h3>
                <p className="text-warm-gray">
                  Adjunta tus archivos por WhatsApp
                </p>
              </div>

              <div className="flex justify-center mb-6">
                <Button
                  onClick={abrirWhatsApp}
                  className="bg-green-500 hover:bg-green-600 text-white py-6 px-8 text-lg font-medium tracking-wide rounded-lg transition-colors duration-300 flex items-center justify-center gap-3 shadow-lg"
                >
                  <MessageCircle size={24} />
                  Enviar por WhatsApp
                </Button>
              </div>

              <div className="text-center border-t border-warm-gray/20 pt-4">
                <p className="text-sm text-warm-gray">
                  También puedes enviarnos tus diseños por correo a{' '}
                  <a href="mailto:dlyrioestampados@gmail.com" className="text-primary-pink font-medium hover:underline">
                    dlyrioestampados@gmail.com
                  </a>
                </p>
                <p className="text-sm text-warm-gray mt-1">
                  con el asunto: <strong className="text-charcoal">"{datosEnviados.nombre} - {datosEnviados.fechaHora}"</strong>
                </p>
              </div>
            </div>

            {/* Botón para hacer otra cotización */}
            <div className="text-center">
              <Button
                onClick={() => {
                  setMostrarConfirmacion(false);
                  setDatosEnviados(null);
                  setProductosEnCotizacion([]);
                  setFormCliente({ nombre: '', email: '', telefono: '', comentarios: '' });
                  // Scroll a la sección de cotización
                  setTimeout(() => {
                    document.getElementById('cotizacion')?.scrollIntoView({ behavior: 'smooth' });
                  }, 100);
                }}
                variant="outline"
                className="border-2 border-primary-pink text-primary-pink hover:bg-primary-pink hover:text-cream-white"
              >
                Hacer otra cotización
              </Button>
            </div>
          </Card>
        </div>
      </section>
    );
  }

  return (
    <section id="cotizacion" className="py-24 bg-soft-gray">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <div className="text-center mb-16">
          <p className="text-primary-pink text-sm tracking-widest uppercase mb-4 font-medium">
            Solicita tu Presupuesto
          </p>
          <h2 className="text-4xl md:text-5xl font-serif text-charcoal mb-6">
            Cotización Personalizada
          </h2>
          <div className="w-24 h-1 bg-primary-pink mx-auto mb-6"></div>
          <p className="text-warm-gray text-lg max-w-2xl mx-auto">
            Agrega todos los productos que necesitas y te contactaremos en menos de 24 horas con tu cotización.
          </p>
        </div>

        {/* Sección 1: Agregar Productos */}
        <Card id="form-producto" className="bg-white border-none shadow-xl p-8 md:p-12 mb-8">
          <div className="flex items-center gap-3 mb-6">
            <div className="w-12 h-12 bg-primary-pink/10 rounded-full flex items-center justify-center">
              <ShoppingBag className="text-primary-pink" size={24} />
            </div>
            <div>
              <h3 className="text-2xl font-serif text-charcoal">
                {productoEditando ? 'Editar Producto' : 'Agregar Producto'}
              </h3>
              <p className="text-warm-gray text-sm">Especifica el producto que deseas cotizar</p>
            </div>
          </div>

          <div className="space-y-6">
            {/* Tipo de Prenda */}
            <div>
              <Label htmlFor="tipoPrenda" className="text-charcoal font-medium mb-2">
                Tipo de Prenda *
              </Label>
              <select
                id="tipoPrenda"
                name="tipoPrenda"
                required
                value={formProducto.tipoPrenda}
                onChange={(e) => setFormProducto(prev => ({...prev, tipoPrenda: e.target.value}))}
                className="w-full h-12 px-4 rounded-md border border-warm-gray/30 focus:border-primary-pink focus:outline-none focus:ring-2 focus:ring-primary-pink/20 bg-white"
              >
                <option value="">Selecciona una prenda</option>
                {tiposPrenda.map((tipo) => (
                  <option key={tipo} value={tipo}>{tipo}</option>
                ))}
              </select>
            </div>

            <div className="grid md:grid-cols-2 gap-6">
              {/* Talle */}
              <div>
                <Label htmlFor="talle" className="text-charcoal font-medium mb-2">
                  Talle *
                </Label>
                <select
                  id="talle"
                  name="talle"
                  required
                  value={formProducto.talle}
                  onChange={(e) => setFormProducto(prev => ({...prev, talle: e.target.value}))}
                  disabled={tallesDisponibles.length === 0}
                  className="w-full h-12 px-4 rounded-md border border-warm-gray/30 focus:border-primary-pink focus:outline-none focus:ring-2 focus:ring-primary-pink/20 bg-white disabled:bg-soft-gray disabled:cursor-not-allowed"
                >
                  <option value="">
                    {tallesDisponibles.length === 0 ? 'Selecciona primero una prenda' : 'Selecciona un talle'}
                  </option>
                  {tallesDisponibles.map((talle) => (
                    <option key={talle} value={talle}>{talle}</option>
                  ))}
                </select>
              </div>

              {/* Color */}
              <div>
                <Label htmlFor="color" className="text-charcoal font-medium mb-2">
                  Color
                </Label>
                <select
                  id="color"
                  name="color"
                  value={formProducto.color}
                  onChange={(e) => setFormProducto(prev => ({...prev, color: e.target.value}))}
                  disabled={coloresDisponibles.length === 0}
                  className="w-full h-12 px-4 rounded-md border border-warm-gray/30 focus:border-primary-pink focus:outline-none focus:ring-2 focus:ring-primary-pink/20 bg-white disabled:bg-soft-gray disabled:cursor-not-allowed"
                >
                  <option value="">
                    {coloresDisponibles.length === 0 ? 'Selecciona primero una prenda' : 'Selecciona un color'}
                  </option>
                  {coloresDisponibles.map((color) => (
                    <option key={color} value={color}>{color}</option>
                  ))}
                </select>
              </div>
            </div>

            {/* Cantidad */}
            <div>
              <Label htmlFor="cantidad" className="text-charcoal font-medium mb-2">
                Cantidad *
              </Label>
              <Input
                id="cantidad"
                name="cantidad"
                type="number"
                min="1"
                value={formProducto.cantidad}
                onChange={(e) => setFormProducto(prev => ({...prev, cantidad: e.target.value}))}
                placeholder="¿Cuántas unidades?"
                required
                className="border-warm-gray/30 focus:border-primary-pink"
              />
            </div>

            {/* Botón Agregar */}
            <Button
              type="button"
              onClick={agregarProducto}
              className="w-full bg-primary-pink hover:bg-deep-navy text-cream-white py-4 text-base font-medium tracking-wide rounded transition-colors duration-300 flex items-center justify-center gap-2"
            >
              <Plus size={20} />
              {productoEditando ? 'ACTUALIZAR PRODUCTO' : 'AGREGAR A LA COTIZACIÓN'}
            </Button>
          </div>
        </Card>

        {/* Sección 2: Lista de Productos */}
        {productosEnCotizacion.length > 0 && (
          <Card className="bg-white border-none shadow-xl p-8 md:p-12 mb-8">
            <div className="flex items-center justify-between mb-6">
              <div>
                <h3 className="text-2xl font-serif text-charcoal">
                  Productos en tu Cotización
                </h3>
                <p className="text-warm-gray text-sm">
                  Revisa los productos antes de enviar
                </p>
              </div>
              <Badge className="bg-primary-pink text-cream-white text-lg px-4 py-2">
                {productosEnCotizacion.length} {productosEnCotizacion.length === 1 ? 'producto' : 'productos'}
              </Badge>
            </div>

            <div className="space-y-3 mb-6">
              {productosEnCotizacion.map((producto) => (
                <div
                  key={producto.id}
                  className="bg-soft-gray p-4 rounded-lg flex items-center justify-between hover:bg-soft-gray/70 transition-colors duration-200"
                >
                  <div className="flex-1">
                    <p className="font-semibold text-charcoal text-lg">
                      {producto.producto}
                    </p>
                    <p className="text-warm-gray text-sm">
                      Talle: <span className="font-medium">{producto.talle}</span>
                      {' • '}
                      Color: <span className="font-medium">{producto.color}</span>
                      {' • '}
                      Cantidad: <span className="font-medium">x{producto.cantidad}</span>
                    </p>
                  </div>
                  <div className="flex gap-2 ml-4">
                    <Button
                      type="button"
                      size="sm"
                      variant="ghost"
                      onClick={() => editarProducto(producto.id)}
                      className="hover:bg-primary-pink/10 hover:text-primary-pink"
                    >
                      <Edit2 size={18} />
                    </Button>
                    <Button
                      type="button"
                      size="sm"
                      variant="ghost"
                      onClick={() => eliminarProducto(producto.id)}
                      className="hover:bg-red-100 hover:text-red-600"
                    >
                      <Trash2 size={18} />
                    </Button>
                  </div>
                </div>
              ))}
            </div>

            {/* Resumen */}
            <div className="border-t border-warm-gray/20 pt-6">
              <div className="flex items-center justify-between mb-4">
                <p className="text-lg font-semibold text-charcoal">
                  Total de prendas:
                </p>
                <p className="text-2xl font-bold text-primary-pink">
                  {totalPrendas}
                </p>
              </div>
              
              {aplicaDescuento && (
                <div className="bg-primary-pink/10 border-2 border-primary-pink rounded-lg p-4">
                  <p className="text-primary-pink font-semibold text-center flex items-center justify-center gap-2">
                    <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                    </svg>
                    ¡Tu pedido califica para precio especial por mayor!
                  </p>
                </div>
              )}
            </div>
          </Card>
        )}

        {/* Sección 3: Datos del Cliente y Envío */}
        <Card className="bg-white border-none shadow-xl p-8 md:p-12">
          <form 
            action="https://formsubmit.co/dlyrioestampados@gmail.com" 
            method="POST"
            onSubmit={handleSubmit}
            encType="multipart/form-data"
          >
            {/* FormSubmit Configuration */}
            <input type="hidden" name="_subject" value={`Cotización - ${formCliente.nombre || 'Cliente'} - ${formatearFechaHora()}`} />
            <input type="hidden" name="_captcha" value="false" />
            <input type="hidden" name="_template" value="table" />
            <input type="text" name="_honey" style={{display: 'none'}} />
            <input type="hidden" name="_next" value={`${window.location.origin}${window.location.pathname}?cotizacion=enviada`} />
            
            {/* Productos en formato texto para el email - cada producto en línea separada */}
            {productosEnCotizacion.map((p, idx) => (
              <input 
                key={p.id}
                type="hidden" 
                name={`Producto ${idx + 1}`} 
                value={`${p.producto} | Talle: ${p.talle} | Color: ${p.color} | Cantidad: ${p.cantidad}`}
              />
            ))}
            <input type="hidden" name="Total de Prendas" value={totalPrendas} />
            <input type="hidden" name="Descuento por Mayor" value={aplicaDescuento ? 'Sí (5+ prendas)' : 'No'} />

            <h3 className="text-2xl font-serif text-charcoal mb-6">
              Tus Datos de Contacto
            </h3>

            <div className="space-y-6">
              {/* Nombre y Teléfono */}
              <div className="grid md:grid-cols-2 gap-6">
                <div>
                  <Label htmlFor="nombre" className="text-charcoal font-medium mb-2">
                    Nombre Completo *
                  </Label>
                  <Input
                    id="nombre"
                    name="Nombre Completo"
                    value={formCliente.nombre}
                    onChange={(e) => setFormCliente(prev => ({...prev, nombre: e.target.value}))}
                    placeholder="Tu nombre"
                    required
                    className="border-warm-gray/30 focus:border-primary-pink"
                  />
                </div>

                <div>
                  <Label htmlFor="telefono" className="text-charcoal font-medium mb-2">
                    Teléfono / WhatsApp *
                  </Label>
                  <Input
                    id="telefono"
                    name="Teléfono/WhatsApp"
                    value={formCliente.telefono}
                    onChange={(e) => {
                      // Solo permitir números y espacios
                      const valor = e.target.value.replace(/[^0-9\s]/g, '');
                      setFormCliente(prev => ({...prev, telefono: valor}));
                    }}
                    placeholder="098 123 456"
                    required
                    inputMode="numeric"
                    className="border-warm-gray/30 focus:border-primary-pink"
                  />
                </div>
              </div>

              {/* Email */}
              <div>
                <Label htmlFor="email" className="text-charcoal font-medium mb-2">
                  Email
                </Label>
                <Input
                  id="email"
                  name="Email"
                  type="email"
                  value={formCliente.email}
                  onChange={(e) => setFormCliente(prev => ({...prev, email: e.target.value}))}
                  placeholder="tu@email.com"
                  className="border-warm-gray/30 focus:border-primary-pink"
                />
              </div>

              {/* Sección informativa para enviar diseños */}
              <div className="bg-gradient-to-br from-primary-pink/5 to-royal-blue/5 border-2 border-dashed border-primary-pink/30 rounded-lg p-6">
                <div className="text-center mb-4">
                  <div className="w-12 h-12 bg-primary-pink/10 rounded-full flex items-center justify-center mx-auto mb-3">
                    <Paperclip className="text-primary-pink" size={24} />
                  </div>
                  <h4 className="text-lg font-serif text-charcoal mb-2">
                    ¿Tienes diseños listos?
                  </h4>
                  <p className="text-warm-gray text-sm">
                    Después de enviar la cotización, podrás adjuntar tus archivos por WhatsApp
                  </p>
                </div>

                <div className="flex justify-center">
                  <Button
                    type="button"
                    disabled={true}
                    className="bg-green-500 text-white py-3 text-sm font-medium rounded-lg flex items-center justify-center gap-2 opacity-50 cursor-not-allowed"
                  >
                    <MessageCircle size={18} />
                    Enviar por WhatsApp
                  </Button>
                </div>

                <p className="text-xs text-warm-gray text-center mt-4">
                  💡 Primero envía tu cotización, luego podrás adjuntar tus diseños
                </p>
                <p className="text-xs text-warm-gray text-center mt-1">
                  También podrás enviarlos por correo a <span className="text-primary-pink">dlyrioestampados@gmail.com</span>
                </p>
              </div>

              {/* Comentarios */}
              <div>
                <Label htmlFor="comentarios" className="text-charcoal font-medium mb-2">
                  Comentarios Adicionales
                </Label>
                <Textarea
                  id="comentarios"
                  name="Comentarios"
                  value={formCliente.comentarios}
                  onChange={(e) => setFormCliente(prev => ({...prev, comentarios: e.target.value}))}
                  placeholder="Describe detalles adicionales, fecha de entrega deseada, etc..."
                  rows={4}
                  className="border-warm-gray/30 focus:border-primary-pink resize-none"
                />
              </div>

              {/* Submit Button */}
              <Button
                type="submit"
                disabled={productosEnCotizacion.length === 0}
                className="w-full bg-primary-pink hover:bg-deep-navy text-cream-white py-4 text-lg font-medium tracking-wide rounded transition-colors duration-300 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                ENVIAR COTIZACIÓN
              </Button>

              <p className="text-xs text-center text-warm-gray mt-4">
                * Campos obligatorios
              </p>
            </div>
          </form>
        </Card>
      </div>
    </section>
  );
};

export default QuoteForm;
